import LogInMessage from "@iewcasia/Components/LogInMessage";
import { OrderTableStyles, styles as commonOrderTableStyles } from "@iewcasia/Components/OrderAcknowledgement";
import OrderDocumentDownload from "@iewcasia/Components/OrderDocumentDownload";
import OrderTablesTitle from "@iewcasia/Components/OrderTablesTitle";
import { getPackingList } from "@iewcasia/Services/OrderService";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import DataTable from "@insite/mobius/DataTable";
import {
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableRow,
    Link,
    Typography,
} from "@insite/mobius/index";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OrderLines {
    warehouse: string;
}

interface OwnProps {
    erpOrderNumber: string;
    orderDate: string;
    orderLines: OrderLines[];
    guid: string;
    status: string;
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        language: state.context.session.language,
        IsAuthenticated: state.context.session.isAuthenticated,
        hideDownloadDocumentButtons: (state.context.settings.settingsCollection as any).myAccountSettings?.hideDownloadDocumentButtons,
    };
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

interface OrderPackingListsStyles extends OrderTableStyles {
    tracking?: InjectableCss;
}

const styles: OrderPackingListsStyles = {
    ...commonOrderTableStyles,
    noResults: {
        css: css`
            text-align: center;
        `,
    },
    tracking: {
        css: css`
            list-style: circle outside none;
            margin-left: 25px;
        `,
    },
};

const OrderPackingLists = ({ erpOrderNumber, status, language, IsAuthenticated, hideDownloadDocumentButtons }: Props) => {
    const [items, setItems] = useState([]);
    const [currentTrackingShown, setCurrentTrackingShown] = useState("" as any);

    const fetchPackingLists = async () => {
        const res = (await getPackingList({ orderNumber: erpOrderNumber })) as any;
        setItems(res?.packingLists || []);
    };

    const renderTracking = (list: any[], id: string) => {
        if (!list?.length) {
            return translate("Not Available");
        }

        if (list.length === 1) {            
                if(list[0].carrierTrackingUrl && list[0].carrierTrackingUrl !== "") {
                    return (
                        <Link href={list[0].carrierTrackingUrl} target="_blank">
                            {list[0].carrierTrackingNumber}
                        </Link>
                    );
                }
                return <Typography>{list[0].carrierTrackingNumber}</Typography>;
        }

        const isExpanded = currentTrackingShown === id;

        return (
            <>
                <Link onClick={() => setCurrentTrackingShown(isExpanded ? "" : id)}>
                    {translate(isExpanded ? "Hide Tracking" : "Show Tracking")}
                </Link>
                {isExpanded && (
                    <Typography as="ul" {...styles.tracking}>
                        {list.map((tracking: any) => (
                            <Typography as="li" key={tracking.carrierTrackingNumber}>
                                {tracking.carrierTrackingUrl && tracking.carrierTrackingUrl !== "" ? (
                                    <Link href={tracking.carrierTrackingUrl} target="_blank">
                                        {tracking.carrierTrackingNumber}
                                    </Link>
                                ) : (
                                    <Typography>{tracking.carrierTrackingNumber}</Typography>
                                )}
                            </Typography>
                        ))}
                    </Typography>
                )}
            </>
        );
    };

    useEffect(() => {
        if (erpOrderNumber && (status === "Open" || status === "Completed")) {
            fetchPackingLists();
        }
    }, []);

    const message = translate("Log In to see Packing Lists");

    return (
        <>
            <OrderTablesTitle text="Packing Lists" />
            {items.length ? (
                <StyledWrapper {...styles.dataTableWrapper}>
                    <DataTable>
                        <DataTableHead>
                            <DataTableHeader>{translate("Date")}</DataTableHeader>
                            <DataTableHeader>{translate("# Of Lines")}</DataTableHeader>
                            <DataTableHeader>{translate("Shipped From")}</DataTableHeader>
                            <DataTableHeader>{translate("Shipment Tracking")}</DataTableHeader>
                            <DataTableHeader>{translate("Document #")}</DataTableHeader>
                            {!hideDownloadDocumentButtons && (
                                <DataTableHeader>{translate("Download")}</DataTableHeader>
                            )}
                        </DataTableHead>
                        <DataTableBody>
                            {items.map((row: any) => (
                                <DataTableRow key={row.id}>
                                    <DataTableCell>
                                        {getLocalizedDateTime({
                                            dateTime: new Date(row.packingDate),
                                            language,
                                        })}
                                    </DataTableCell>
                                    <DataTableCell>{row.numberOfLines}</DataTableCell>
                                    <DataTableCell>{row.sourceLocation}</DataTableCell>
                                    <DataTableCell>{renderTracking(row.shipmentTrackings, row.id)}</DataTableCell>
                                    <DataTableCell>{row.packingNumber}</DataTableCell>
                                    {!hideDownloadDocumentButtons && (
                                        <DataTableCell>
                                            <OrderDocumentDownload
                                                type="packing list"
                                                name={`packing_slip_${row.packingNumber}`}
                                                id={row.id}
                                            />
                                        </DataTableCell>
                                    )}
                                </DataTableRow>
                            ))}
                        </DataTableBody>
                    </DataTable>
                </StyledWrapper>
            ) : (
                <>
                    {IsAuthenticated && (
                        <Typography as="p" variant="p" {...styles.noResults}>
                            {translate("No Documents Available")}
                        </Typography>
                    )}
                    {!IsAuthenticated && (
                        <Typography as="p" variant="p" {...styles.noResults}>
                            <LogInMessage title={message} />
                        </Typography>
                    )}
                </>
            )}
        </>
    );
};

export default connect(mapStateToProps)(OrderPackingLists);
